import React, { useEffect, useState } from "react";
import {
  Button,
  ButtonGroup,
  Col,
  Container,
  FormSelect,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Row,
} from "shards-react";
import { getHistoricGraphData } from "../backend/BinAlgData";
import { parseDate } from "../backend/common";
import PageTitle from "../components/common/PageTitle";
import SmallStats from "../components/common/SmallStats2";
import WeeklyDatePicker from "../components/common/WeeklyDatePicker";
import FormNewHistoric from "../components/forms/FormNewHistoric";
import { Animals, VACUNO_MACHO_KEY } from "../utils/constants";

const moment = require("moment");

export const NewHistoric = () => {
  const [fromDate, setFromDate] = useState(
    moment().startOf("isoWeek").subtract(9, "week")
  );
  const [toDate, setToDate] = useState(moment().startOf("isoWeek").add(1, "w"));

  const [animalType, setAnimalType] = useState({
    key: VACUNO_MACHO_KEY,
    sex: Animals.bovineMale.sex,
    weight: Animals.bovineMale.weight[2],
    fatType: Animals.bovineMale.fatType[2],
    type: Animals.bovineMale.type,
  });

  const [prices, setPrices] = useState([]);
  const [priceVariations, setPriceVariations] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [prediction4Weeks, setPrediction4Weeks] = useState([]);
  const [diff4Weeks, setDiff4Weeks] = useState([]);
  const [dates, setDates] = useState([]);

  // Precision for price variations and predictions
  const [precisionIA, setPrecisionIA] = useState(undefined);
  const [precisionBinefar, setPrecisionBinefar] = useState(undefined);
  const [thresholdPrecision, setThresholdPrecision] = useState(0.03);

  const getData = () => {
    // Set default values
    setPrices([]);
    setPriceVariations([]);
    setPredictions([]);
    setPrediction4Weeks([]);
    setDiff4Weeks([]);
    setPrecisionIA(undefined);
    setPrecisionBinefar(undefined);
    // Get data from backend
    getHistoricGraphData(
      {
        sex: animalType.sex,
        weight: animalType.weight,
        fatType: animalType.fatType,
        type: animalType.type,
        from: fromDate.clone().format("DD-MM-YYYY"),
        to: toDate.clone().format("DD-MM-YYYY"),
      },
      (result) => {
        const priceResult = result.binefar;
        const variationResult = result.variation;
        const predictionResult = result.predicted;
        const prediction4WeeksResult = result.predicted4weeks;
        // Get date range from fromDate to toDate
        let datesR = [];
        let date = fromDate.clone();
        while (date.isSameOrBefore(toDate)) {
          // Format: 2023-10-02T00:00:00.000Z
          datesR.push(date.format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"));
          date.add(1, "week");
        }
        // Fit the data to the date range
        const priceR = datesR.map((date) => {
          return (
            priceResult.find((val) => val.date.standard === date)?.price ??
            undefined
          );
        });
        const variationR = datesR.map((date) => {
          return (
            variationResult.find((val) => val.date.standard === date)
              ?.variation ?? undefined
          );
        });
        const predictionR = datesR.map((date) => {
          return (
            predictionResult.find((val) => val.date.standard === date)?.price ??
            undefined
          );
        });
        const prediction4WeeksR = datesR.map((date) => {
          return (
            prediction4WeeksResult.find((val) => val.date.standard === date)
              ?.probabilities ?? undefined
          );
        });

        // Remove the last date as it is
        // Calculate the price variation between current price and variation
        const firstPrice = priceResult.at(0).price;
        const firstVariation = variationR.at(0);
        const priceVariationR = variationR.map((val, index) => {
          const diff = val + priceR[index];
          return isNaN(diff) ? undefined : diff.toFixed(2);
        });
        priceVariationR.pop();
        // Set the state
        setDates(datesR);
        setPrices(priceR);
        setPredictions(predictionR);
        setPrediction4Weeks(prediction4WeeksR);
        const firstPriceVariation = firstPrice + firstVariation;
        setPriceVariations([
          isNaN(firstPriceVariation)
            ? undefined
            : firstPriceVariation.toFixed(2),
          ...priceVariationR,
        ]);
      }
    );
  };

  useEffect(() => {
    getData();
  }, [animalType, fromDate, toDate]);

  useEffect(() => {
    // Calculate precisions
    const precisionIAR =
      (predictions.reduce((acc, val, index) => {
        if (val !== undefined && !isNaN(val)) {
          const diff = Math.abs(val - prices[index]);
          return acc + (diff < thresholdPrecision ? 1 : 0);
        }
        return acc;
      }, 0) /
        (predictions.filter((val) => val !== undefined && !isNaN(val)).length -
          1)) *
      100;

    const precisionBinefarR =
      (priceVariations.reduce((acc, val, index) => {
        if (val !== undefined && !isNaN(val)) {
          const diff = Math.abs(val - prices[index]);
          return acc + (diff < thresholdPrecision ? 1 : 0);
        }
        return acc;
      }, 0) /
        (priceVariations.filter((val) => val !== undefined && !isNaN(val))
          .length -
          1)) *
      100;
    setPrecisionIA(precisionIAR.toFixed(2));
    setPrecisionBinefar(precisionBinefarR.toFixed(2));
  }, [prices, priceVariations, predictions, thresholdPrecision]);

  useEffect(() => {
    // Compute the difference between the current price and 4 weeks ahead price
    const diff4WeeksR = prices.map((price, index) => {
      const currentPrice = price;
      const price4WeeksAhead = prices.at(index + 4);
      if (currentPrice === undefined || price4WeeksAhead === undefined) {
        return undefined;
      }
      const diff = (price4WeeksAhead - currentPrice).toFixed(2);
      // Return the trend based on the difference
      if (diff < -0.03) return "down";
      if (diff > 0.03) return "up";
      return "maintain";
    });
    setDiff4Weeks(diff4WeeksR);
  }, [prices, prediction4Weeks]);

  // Helper function to calculate min/max with conditional limits
  const getChartLimits = () => {
    const allData = [
      ...prices.filter((val) => val !== undefined && !isNaN(val)),
      ...priceVariations.filter((val) => val !== undefined && !isNaN(val)),
      ...predictions.filter((val) => val !== undefined && !isNaN(val)),
    ];

    const validData = allData.filter((val) => val !== undefined && !isNaN(val));
    if (validData.length === 0) return { min: undefined, max: undefined };

    const minValue = Math.min(...validData);
    const maxValue = Math.max(...validData);

    // Only set limits if the range is less than 0.4
    return {
      min: minValue - 0.05,
      max: maxValue + 0.05,
    };
  };

  const getChartLimitsDiff = () => {
    const allData = [
      ...prices.map((val, index) => {
        const diff = priceVariations[index] - val;
        return isNaN(diff) ? 0 : diff;
      }),
      ...predictions.map((val, index) => {
        const diff = val - prices[index];
        return isNaN(diff) ? 0 : diff;
      }),
    ];

    const validData = allData.filter((val) => val !== undefined && !isNaN(val));
    if (validData.length === 0) return { min: undefined, max: undefined };

    const minValue = Math.min(...validData);
    const maxValue = Math.max(...validData);

    return {
      min: minValue - 0.01,
      max: maxValue + 0.01,
    };
  };

  // Calculate limits for the first chart (all price data combined)

  const firstChartLimits = getChartLimits();
  const secondChartLimits = getChartLimitsDiff();

  return (
    <Container fluid className="main-content-container pb-4">
      <PageTitle
        title="Nuevo Precio de Lonja"
        md="12"
        className="ml-sm-auto mr-sm-auto"
      />
      <Col sm="12">
        <FormNewHistoric
          animalType={animalType}
          setAnimalType={setAnimalType}
          refresh={getData}
          // Get last prediction which is not undefined
          lastPrediction={predictions
            ?.slice()
            .reverse()
            .find((val) => val)}
        />
        <Row>
          <Col md="12" lg="6">
            <label>Fecha</label>
            <InputGroup className="flex-wrap">
              <WeeklyDatePicker date={fromDate} changeDate={setFromDate} />
              <WeeklyDatePicker date={toDate} changeDate={setToDate} />
              <InputGroupAddon type="append">
                <InputGroupText>
                  <i className="material-icons">&#xE916;</i>
                </InputGroupText>
              </InputGroupAddon>
            </InputGroup>
          </Col>
          <Col md="12" lg="2">
            <label>Animal</label>
            <InputGroup>
              <FormSelect
                className="mb-2"
                onChange={(event) => {
                  const animal = JSON.parse(event.target.value);
                  setAnimalType({
                    key: event.target.options[
                      event.target.selectedIndex
                    ].getAttribute("data-key"),
                    sex: animal.sex,
                    weight: Object.values(animal.weight)[0],
                    fatType: Object.values(animal.fatType)[0],
                    type: animal.type,
                  });
                }}
              >
                {Object.entries(Animals).map((val) => (
                  <option
                    key={val[0]}
                    data-key={val[0]}
                    value={JSON.stringify(val[1])}
                  >
                    {val[1].name}
                  </option>
                ))}
              </FormSelect>
            </InputGroup>
          </Col>
          <Col md="12" lg="2">
            <label>Categoría de pesos</label>
            <InputGroup>
              <FormSelect
                className="mb-2"
                value={animalType.weight}
                onChange={(event) => {
                  setAnimalType({
                    ...animalType,
                    weight: event.target.value,
                  });
                }}
              >
                {Object.entries(Animals[animalType.key].weight).map((val) => (
                  <option key={val[0]} value={val[1]}>
                    {val[1]}
                  </option>
                ))}
              </FormSelect>
            </InputGroup>
          </Col>
          <Col md="12" lg="2">
            <label>Categoría de grasa</label>
            <InputGroup>
              <FormSelect
                value={animalType.fatType}
                onChange={(event) =>
                  setAnimalType({ ...animalType, fatType: event.target.value })
                }
              >
                {Object.values(Animals[animalType.key].fatType).map(
                  (fatType) => (
                    <option key={fatType} value={fatType}>
                      {fatType}
                    </option>
                  )
                )}
              </FormSelect>
            </InputGroup>
          </Col>
        </Row>
      </Col>
      <Col>
        <SmallStats
          id={`small-stats-0`}
          variation="2"
          chartData={{
            labels: dates.map((date) => parseDate(date)),
            datasets: [
              {
                label: "Real Binéfar",
                fill: "start",
                borderWidth: 1.5,
                backgroundColor: "rgba(255,180,0,0.6)",
                borderColor: "rgba(255,180,0)",
                data: prices,
                align: "start",
                anchor: "start",
                display: true,
              },
              {
                label: "Tendencia Binéfar",
                fill: "start",
                borderWidth: 1.5,
                backgroundColor: "rgba(19,196,24,0.6)",
                borderColor: "rgba(19,196,24)",
                // For each date, sum the current variation to the previous price
                data: priceVariations,
                align: "start",
                anchor: "start",
                display: "auto",
              },
              {
                label: "Predicción",
                backgroundColor: "rgba(255,65,105,0.6)",
                borderColor: "rgba(255,65,105,1)",
                data: predictions,
                align: "end",
                anchor: "end",
                fill: "start",
                display: "auto",
              },
            ],
          }}
          dates={dates}
          max={firstChartLimits.max}
          min={firstChartLimits.min}
          thresholdPrecision={thresholdPrecision}
          precisionIA={precisionIA}
          precisionBinefar={precisionBinefar}
          prediction4Weeks={prediction4Weeks}
          pricesDiff4Weeks={diff4Weeks}
          label={`Predicción ${Animals[animalType.key].name} ${
            animalType.weight
          } ${animalType.fatType}`}
        />
        <SmallStats
          id={`small-stats-0`}
          variation="2"
          chartData={{
            labels: dates,
            datasets: [
              {
                label: "Tendencia Binéfar",
                fill: "start",
                borderWidth: 1.5,
                backgroundColor: "rgba(19,196,24,0.6)",
                borderColor: "rgba(19,196,24)",
                data: prices.map((val, index) => {
                  const diff = priceVariations[index] - val;
                  return isNaN(diff) ? undefined : diff.toFixed(3);
                }),
                align: "start",
                anchor: "start",
                display: "auto",
              },
              {
                label: "Predicción",
                backgroundColor: "rgba(255,65,105,0.6)",
                borderColor: "rgba(255,65,105,1)",
                data: predictions.map((val, index) => {
                  const diff = val - prices[index];
                  return isNaN(diff) ? undefined : diff.toFixed(3);
                }),
                align: "end",
                anchor: "end",
                fill: "start",
                display: true,
              },
            ],
          }}
          max={secondChartLimits.max}
          min={secondChartLimits.min}
          label={"Diferencia Real-Predicción"}
        />
      </Col>
    </Container>
  );
};
export default NewHistoric;
